import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Crown, Lock, Check, Sparkles, ArrowRight, Building2, 
  Compass, GitMerge, Database, RefreshCw, Lightbulb, Layers,
  Star, Zap, Shield, Users, TrendingUp, Target, ChevronRight,
  Loader2, ExternalLink, CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/PageHeader';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/lib/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for premium agents
const PREMIUM_ICONS = {
  compass: Compass,
  'git-merge': GitMerge,
  database: Database,
  'refresh-cw': RefreshCw,
  lightbulb: Lightbulb,
  layers: Layers,
};

// Color mapping
const COLOR_MAP = {
  amber: 'from-amber-500 to-orange-500',
  violet: 'from-violet-500 to-purple-500',
  emerald: 'from-emerald-500 to-teal-500',
  rose: 'from-rose-500 to-pink-500',
  yellow: 'from-yellow-500 to-amber-500',
  sky: 'from-sky-500 to-blue-500',
};

const PRICING_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: '299',
    period: '/Monat',
    description: 'Für kleine Teams',
    features: [
      '2 Premium-Agenten',
      '100 Anfragen/Monat',
      'E-Mail Support',
      'Basis Analytics',
    ],
    highlighted: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '699',
    period: '/Monat',
    description: 'Für wachsende Unternehmen',
    features: [
      'Alle 6 Premium-Agenten',
      'Unbegrenzte Anfragen',
      'Priority Support',
      'Advanced Analytics',
      'Custom Prompts',
      'API Zugang',
    ],
    highlighted: true,
    badge: 'Beliebt',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 'Individuell',
    period: '',
    description: 'Für Großunternehmen',
    features: [
      'Alles aus Professional',
      'Dedicated Account Manager',
      'Custom AI Training',
      'On-Premise Option',
      'SLA Garantie',
      'White-Label',
    ],
    highlighted: false,
  },
];

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } 
};

export default function CorporateDevelopmentPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [premiumAgents, setPremiumAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    loadPremiumAgents();
    if (user?.id) {
      loadSubscriptionStatus();
    }
  }, [user]);

  // Check for successful checkout return
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId && user?.id) {
      verifyCheckoutSession(sessionId);
    }
  }, [searchParams, user]);

  const loadPremiumAgents = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/ai/premium-agents`);
      setPremiumAgents(res.data || []);
    } catch (err) {
      console.error('Failed to load premium agents:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/stripe/subscription-status/${user.id}`);
      setSubscription(res.data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    }
  };

  const verifyCheckoutSession = async (sessionId) => {
    setVerifying(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/stripe/verify-session?session_id=${sessionId}&user_id=${user.id}`);
      if (res.data.success) {
        toast.success('🎉 Premium erfolgreich aktiviert!', {
          description: `Plan: ${res.data.plan === 'professional' ? 'Professional' : 'Starter'}`
        });
        loadSubscriptionStatus();
      }
    } catch (err) {
      console.error('Verification failed:', err);
      toast.error('Verifizierung fehlgeschlagen');
    } finally {
      setVerifying(false);
    }
  };

  const handleCheckout = async (planId) => {
    if (planId === 'enterprise') {
      toast.info('Bitte kontaktieren Sie uns für Enterprise-Pläne', {
        description: 'info@markipilot.com'
      });
      return;
    }

    if (!user?.id || !user?.email) {
      toast.error('Bitte melden Sie sich an');
      return;
    }

    setCheckoutLoading(true);
    try {
      const currentUrl = window.location.origin + window.location.pathname;
      
      const res = await axios.post(`${BACKEND_URL}/api/stripe/create-checkout-session`, {
        plan_id: planId,
        user_email: user.email,
        user_id: user.id,
        success_url: currentUrl,
        cancel_url: currentUrl
      });

      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error('Checkout fehlgeschlagen', {
        description: err.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!user?.id) return;
    
    try {
      const returnUrl = window.location.origin + window.location.pathname;
      const res = await axios.post(`${BACKEND_URL}/api/stripe/create-portal-session?user_id=${user.id}&return_url=${encodeURIComponent(returnUrl)}`);
      
      if (res.data.portal_url) {
        window.location.href = res.data.portal_url;
      }
    } catch (err) {
      console.error('Portal session failed:', err);
      toast.error('Fehler beim Öffnen des Kundenportals');
    }
  };

  const getAgentIcon = (iconName) => {
    return PREMIUM_ICONS[iconName] || Sparkles;
  };

  const hasPremium = subscription?.has_premium || subscription?.status === 'active';

  return (
    <motion.div 
      initial="hidden" 
      animate="visible" 
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
    >
      <PageHeader 
        title="Unternehmensentwicklung" 
        description="Premium KI-Agenten für strategische Entscheidungen" 
        icon={<Building2 className="w-6 h-6" />}
      />

      {/* Verification Loading */}
      {verifying && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto mb-4" />
            <p className="font-medium text-slate-900">Zahlung wird verifiziert...</p>
          </div>
        </div>
      )}

      {/* Active Subscription Banner */}
      {hasPremium && (
        <motion.div 
          variants={fadeIn}
          className="mb-6 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 p-4 text-white"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Crown className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Premium aktiv</p>
                <p className="text-sm text-white/80">
                  Plan: {subscription?.plan === 'professional' ? 'Professional' : 'Starter'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="sm"
                onClick={() => window.open('/premium-anleitung.html', '_blank')}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Anleitung (PDF)
              </Button>
              <Button 
                variant="secondary" 
                size="sm"
                onClick={handleManageSubscription}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Abo verwalten
              </Button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Hero Section */}
      <motion.div 
        variants={fadeIn}
        className="relative mb-8 rounded-2xl overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-amber-600 via-orange-600 to-rose-600" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M0%200h60v60H0z%22%20fill%3D%22none%22%2F%3E%3Cpath%20d%3D%22M30%2030m-2%200a2%202%200%201%200%204%200a2%202%200%201%200-4%200%22%20fill%3D%22rgba(255%2C255%2C255%2C0.1)%22%2F%3E%3C%2Fsvg%3E')] opacity-30" />
        
        <div className="relative px-8 py-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-6 h-6 text-amber-200" />
            <span className="text-sm font-medium text-amber-200 uppercase tracking-wide">Premium Feature</span>
          </div>
          
          <h2 className="font-display text-3xl font-bold mb-3">
            Strategische KI für Ihr Unternehmen
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mb-6">
            Greifen Sie auf Elite-Berater-Level KI-Agenten zu, die Ihr Unternehmen 
            bei strategischen Entscheidungen, M&A, Innovation und Transformation unterstützen.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Check className="w-4 h-4 text-green-300" />
              <span className="text-sm">McKinsey/BCG-Level Frameworks</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Check className="w-4 h-4 text-green-300" />
              <span className="text-sm">6 spezialisierte Agenten</span>
            </div>
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-2">
              <Check className="w-4 h-4 text-green-300" />
              <span className="text-sm">Exklusives Wissen</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Premium Agents Grid */}
      <motion.div variants={fadeIn} className="mb-12">
        <h3 className="font-display text-xl font-semibold text-slate-900 mb-6">
          Premium KI-Agenten
        </h3>
        
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-48 rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {premiumAgents.map((agent, index) => {
              const Icon = getAgentIcon(agent.icon);
              const colorClass = COLOR_MAP[agent.color] || COLOR_MAP.amber;
              const isUnlocked = hasPremium;
              
              return (
                <motion.div
                  key={agent.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={cn(
                    "group relative bg-white rounded-xl border p-6 transition-all duration-300",
                    isUnlocked 
                      ? "border-emerald-200 hover:shadow-lg hover:border-emerald-300" 
                      : "border-slate-200 hover:shadow-lg hover:border-amber-200"
                  )}
                >
                  {/* Badge */}
                  <div className="absolute top-3 right-3">
                    {isUnlocked ? (
                      <div className="flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium">
                        <Check className="w-3 h-3" />
                        Freigeschaltet
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">
                        <Crown className="w-3 h-3" />
                        Premium
                      </div>
                    )}
                  </div>
                  
                  {/* Icon */}
                  <div className={cn(
                    "w-12 h-12 rounded-xl bg-gradient-to-br flex items-center justify-center mb-4 shadow-lg",
                    colorClass
                  )}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h4 className="font-display font-semibold text-slate-900 mb-1">
                    {agent.name}
                  </h4>
                  <p className="text-sm text-slate-500 mb-3">
                    {agent.description}
                  </p>
                  
                  {/* Tools */}
                  {agent.tools && (
                    <p className="text-xs text-slate-400 mb-4">
                      <span className="font-medium">Tools:</span> {agent.tools}
                    </p>
                  )}
                  
                  {/* CTA */}
                  {isUnlocked ? (
                    <Button 
                      size="sm"
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => window.location.href = '/admin/ai-chat'}
                    >
                      <Sparkles className="w-3 h-3 mr-2" />
                      Jetzt nutzen
                    </Button>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full group-hover:bg-amber-50 group-hover:text-amber-700 group-hover:border-amber-200"
                      onClick={() => handleCheckout('professional')}
                      disabled={checkoutLoading}
                    >
                      {checkoutLoading ? (
                        <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                      ) : (
                        <Lock className="w-3 h-3 mr-2" />
                      )}
                      Freischalten
                    </Button>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Value Proposition */}
      <motion.div variants={fadeIn} className="mb-12">
        <div className="bg-slate-50 rounded-2xl p-8">
          <h3 className="font-display text-xl font-semibold text-slate-900 mb-6 text-center">
            Warum Unternehmen diese Kompetenzen intern aufbauen
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Zap, title: 'Schnellere Entscheidungen', desc: 'Direkter Zugang zu strategischem Know-how' },
              { icon: Shield, title: 'Unabhängigkeit', desc: 'Weniger Abhängigkeit von externen Beratungen' },
              { icon: TrendingUp, title: 'Wettbewerbsvorteile', desc: 'Nachhaltige strategische Vorteile aufbauen' },
              { icon: Users, title: 'Internes Know-how', desc: 'Wissen bleibt im Unternehmen statt in PowerPoints' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center mx-auto mb-3">
                  <item.icon className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-medium text-slate-900 mb-1">{item.title}</h4>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Pricing Section - Only show if not premium */}
      {!hasPremium && (
        <motion.div variants={fadeIn} className="mb-8">
          <h3 className="font-display text-xl font-semibold text-slate-900 mb-2 text-center">
            Wählen Sie Ihren Plan
          </h3>
          <p className="text-slate-500 text-center mb-8">
            Investieren Sie in strategische KI für nachhaltiges Wachstum
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {PRICING_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={cn(
                  "relative rounded-2xl border p-6 transition-all",
                  plan.highlighted 
                    ? "border-amber-300 bg-gradient-to-b from-amber-50 to-white shadow-lg scale-105" 
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      {plan.badge}
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h4 className="font-display text-lg font-semibold text-slate-900 mb-1">
                    {plan.name}
                  </h4>
                  <p className="text-sm text-slate-500 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    {plan.price !== 'Individuell' && <span className="text-slate-400 text-lg">CHF</span>}
                    <span className="font-display text-4xl font-bold text-slate-900">{plan.price}</span>
                    <span className="text-slate-400">{plan.period}</span>
                  </div>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                      <Check className={cn(
                        "w-4 h-4 flex-shrink-0",
                        plan.highlighted ? "text-amber-500" : "text-green-500"
                      )} />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button
                  onClick={() => handleCheckout(plan.id)}
                  disabled={checkoutLoading}
                  className={cn(
                    "w-full",
                    plan.highlighted 
                      ? "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white" 
                      : "bg-slate-900 hover:bg-slate-800 text-white"
                  )}
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {plan.id === 'enterprise' ? 'Kontakt aufnehmen' : 'Jetzt starten'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-6">
            <CreditCard className="w-4 h-4 inline mr-1" />
            Sichere Zahlung über Stripe • Jederzeit kündbar
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}
